import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { apiClient, unwrap } from "../../api/client";
import type { components } from "../../api/generated/vision-api";

export type Device = components["schemas"]["Device"];
export type Space = components["schemas"]["Space"];
export type Group = components["schemas"]["Group"];

export type DeviceFilter = { spaceId?: string; groupId?: string };

const PAGE = 500; // the contract's maximum

export const devicesQuery = (orgId: string, filter: DeviceFilter) =>
  infiniteQueryOptions({
    queryKey: ["org", orgId, "/v1/devices", filter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        apiClient("customers").GET("/v1/devices", {
          params: {
            query: { limit: PAGE, cursor: pageParam, space_id: filter.spaceId, group_id: filter.groupId },
          },
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export async function allPages<T>(
  fetchPage: (cursor?: string) => Promise<{ items: T[]; next_cursor?: string | null }>,
) {
  const items: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await fetchPage(cursor);
    items.push(...page.items);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  return items;
}

/** Every space of the organisation the actor may see, for names in the list and the space pickers. */
export const spacesQuery = (orgId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/spaces"],
    queryFn: () =>
      allPages((cursor) =>
        unwrap(apiClient("customers").GET("/v1/spaces", { params: { query: { limit: PAGE, cursor } } })),
      ),
    staleTime: 60_000,
  });

export const groupsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/groups"],
    queryFn: () =>
      allPages((cursor) =>
        unwrap(apiClient("customers").GET("/v1/groups", { params: { query: { limit: PAGE, cursor } } })),
      ),
    staleTime: 60_000,
  });

/** "Farm › Field 3": the space's path, for pickers and the list. */
export function spacePath(spaces: Space[], id: string | null | undefined): string {
  if (!id) return "";
  const byId = new Map(spaces.map((s) => [s.id, s]));
  const names: string[] = [];
  let s = byId.get(id);
  for (let guard = 0; s && guard < 32; guard++) {
    names.unshift(s.name);
    s = s.parent_id ? byId.get(s.parent_id) : undefined;
  }
  return names.join(" › ");
}

/** Spaces in tree order (parents before their children, siblings by name), for a picker. */
export function spacesInTreeOrder(spaces: Space[]): Space[] {
  const children = new Map<string | null, Space[]>();
  for (const s of spaces) {
    const key = s.parent_id && spaces.some((p) => p.id === s.parent_id) ? s.parent_id : null;
    children.set(key, [...(children.get(key) ?? []), s]);
  }
  const out: Space[] = [];
  const visit = (parent: string | null) => {
    for (const s of (children.get(parent) ?? []).sort((a, b) => a.name.localeCompare(b.name))) {
      out.push(s);
      visit(s.id);
    }
  };
  visit(null);
  return out;
}

/** Free-text search over what the list shows (WEB-F-DEV-01); the contract has no search parameter yet. */
/** The names of the groups a device is in, as the Groups column shows them (accounts-places-sharing CL-04). */
export function groupNames(
  groups: readonly { id: string; name: string }[],
  ids: readonly string[] | undefined,
): string {
  if (!ids?.length) return "";
  const byId = new Map(groups.map((g) => [g.id, g.name]));
  return ids
    .map((id) => byId.get(id))
    .filter((n): n is string => !!n)
    .sort((a, b) => a.localeCompare(b))
    .join(", ");
}

export function matchesSearch(d: Device, spaceName: string, q: string, groupName = ""): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [d.name, d.device_id, d.serial, d.product, d.function, spaceName, groupName, d.fw_version].some((v) =>
    v?.toLowerCase().includes(needle),
  );
}
